// ===================================================================
// The Crowd v2 — Catalogue des modules
//
// Source unique de ce qu'affichent l'accueil v2 et les pages de
// regroupement. Une entree d'accueil est soit un module (route directe),
// soit un regroupement (page intermediaire avec ses sous-cartes).
//
// Visibilite : `roles` liste les valeurs de Collaborateurs.role admises,
// `null` = tout le monde. Elle reprend la logique actuelle de App.js.
// `condition` (optionnelle) : fonction (collaborateur) -> booleen, pour
// une regle propre au collaborateur (ex. dispense de saisie des temps).
// C'est une commodite d'affichage, PAS une protection : l'autorisation
// reelle releve du backend (Chantier 3 — RBAC applicatif, pas encore fait).
//
// `vue` : la vue de l'ancienne interface dans laquelle vit la route
// ("collaborateur" ou "manager"). Tant que les modules ne sont pas
// branches sur la v2, App.js bascule sur cette vue avant de naviguer.
// `null` = route presente dans les deux vues, on ne touche a rien.
//
// `modules` (sur un module) : sous-cartes. Le module s'ouvre alors sur
// une page intermediaire a sa routeV2 (ex. Comptes AD -> Creation,
// Historique, Gestion), rendue par GroupeV2.
//
// `routeV2` : renseigne des qu'un module est branche sur la v2. La
// carte ouvre alors cette route, dans la coque v2, au lieu de l'ancienne
// page. Absent = le module s'ouvre encore dans l'interface actuelle.
// ===================================================================

export const ROLES_PILOTAGE = ["manager", "directeur"];

// Collaborateurs.saisie_temps : absent ou vrai = saisit ses temps
export function saisitSesTemps(collaborateur) {
  return collaborateur?.saisie_temps !== false;
}

// Tracés des icones (viewBox 0 0 16 16, trait 1.5)
const ICONES = {
  horloge: "M8 2.8a5.2 5.2 0 1 0 0 10.4A5.2 5.2 0 0 0 8 2.8ZM8 5.2v3l2 1.4",
  bulle: "M13.2 8.4c0 2.5-2.3 4.5-5.2 4.5-.8 0-1.6-.2-2.3-.5l-3 1 1-2.5A4.2 4.2 0 0 1 2.8 8.4c0-2.5 2.3-4.5 5.2-4.5s5.2 2 5.2 4.5Z",
  livres: "M3 3h3.2v10H3ZM7.4 3h3.2v10H7.4ZM11.6 3.6l2.2.6-2.4 9.2-2.2-.6",
  utilisateurs: "M6.4 3.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4ZM2.6 12.6c0-2 1.7-3.2 3.8-3.2s3.8 1.2 3.8 3.2M12 6v4M10 8h4",
  echange: "M3 5.5h8.5L9.5 3.5M13 10.5H4.5l2 2",
  barres: "M2.8 13.2h10.4M4.4 11V8M8 11V4.6M11.6 11V6.6",
  dossier: "M2.2 4.4h4l1.2 1.5h6.4v6.4H2.2Z",
  ticket: "M2.6 5.2h10.8v2a1.2 1.2 0 0 0 0 2.4v2H2.6v-2a1.2 1.2 0 0 0 0-2.4ZM9.6 5.2v6.4",
  equipe: "M6.4 3.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4ZM2.6 12.6c0-2 1.7-3.2 3.8-3.2s3.8 1.2 3.8 3.2M11 5.2a2 2 0 0 1 0 3.9M12.2 12.6c0-1.2-.5-2.1-1.3-2.7",
  liste: "M3 4.5h1.5M3 8h1.5M3 11.5h1.5M6.5 4.5H13M6.5 8H13M6.5 11.5H13",
  grille: "M2.8 2.8h4.4v4.4H2.8ZM8.8 2.8h4.4v4.4H8.8ZM2.8 8.8h4.4v4.4H2.8ZM8.8 8.8h4.4v4.4H8.8Z",
  plus: "M6.4 3.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4ZM2.6 12.6c0-2 1.7-3.2 3.8-3.2s3.8 1.2 3.8 3.2M12 6v4M10 8h4",
  annuaire: "M4 2.6h8.4v10.8H4ZM2.8 5h2.4M2.8 8h2.4M2.8 11h2.4M8.2 6.4a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6ZM6.4 11.4c.2-1.1 1-1.8 1.8-1.8s1.6.7 1.8 1.8",
};

// --- Regroupements : une carte d'accueil qui ouvre une page intermediaire

export const GROUPES = {
  activite: {
    id: "activite",
    titre: "Mon activité",
    description: "Saisie des temps, mes projets, mes étapes et mes tickets.",
    icone: ICONES.horloge,
    roles: null,
    modules: [
      { id: "saisie", titre: "Saisie des temps", description: "Déclaration du temps passé.", icone: ICONES.horloge, route: "/saisie", routeV2: "/v2/activite/saisie", vue: "collaborateur", roles: null, condition: saisitSesTemps },
      { id: "mes-projets", titre: "Mes projets", description: "Mes objectifs et les projets où j'interviens en appui.", icone: ICONES.dossier, route: "/projets", routeV2: "/v2/activite/projets", vue: "collaborateur", roles: null },
      { id: "mes-etapes", titre: "Mes étapes", description: "Les étapes de mes projets, par échéance.", icone: ICONES.liste, route: null, routeV2: "/v2/activite/etapes", vue: "collaborateur", roles: null },
      { id: "mes-tickets", titre: "Mes tickets", description: "Mes tickets Jira.", icone: ICONES.ticket, route: "/jira", routeV2: "/v2/activite/tickets", vue: "collaborateur", roles: null },
    ],
  },
  utilisateurs: {
    id: "utilisateurs",
    titre: "Gestion utilisateurs",
    description: "Accueil des nouveaux arrivants, comptes Active Directory et suivi des opérations.",
    icone: ICONES.utilisateurs,
    roles: null,
    modules: [
      { id: "creation-compte", titre: "Accueil nouvel arrivant", description: "Envoi du mail d'information au nouvel arrivant et à ses managers.", icone: ICONES.plus, route: "/creation-compte", routeV2: "/v2/utilisateurs/accueil", vue: null, roles: null },
      {
        id: "comptes-ad",
        titre: "Comptes AD",
        description: "Création, historique et référentiels des comptes Active Directory.",
        icone: ICONES.annuaire,
        route: "/comptes-ad",
        routeV2: "/v2/utilisateurs/comptes-ad",
        vue: null,
        roles: null,
        modules: [
          { id: "ad-creation", titre: "Création", description: "Demandes Odoo et nouvelle demande de compte.", icone: ICONES.plus, routeV2: "/v2/utilisateurs/comptes-ad/creation", roles: null },
          { id: "ad-historique", titre: "Historique", description: "Suivi des demandes, étapes et journal.", icone: ICONES.horloge, routeV2: "/v2/utilisateurs/comptes-ad/historique", roles: null },
          { id: "ad-gestion", titre: "Gestion", description: "Administration des référentiels.", icone: ICONES.grille, routeV2: "/v2/utilisateurs/comptes-ad/gestion", roles: ["directeur"] },
        ],
      },
      { id: "suivi-operations", titre: "Suivi", description: "Créations, modifications et désactivations de comptes, au jour le jour.", icone: ICONES.barres, route: null, routeV2: "/v2/utilisateurs/suivi", vue: null, roles: null },
    ],
  },
  pilotage: {
    id: "pilotage",
    titre: "Pilotage",
    description: "Équipes, projets, tâches et tableaux de bord.",
    icone: ICONES.barres,
    roles: ROLES_PILOTAGE,
    modules: [
      { id: "equipe", titre: "Équipes", description: "Collaborateurs et fiches individuelles.", icone: ICONES.equipe, route: "/equipe", routeV2: "/v2/pilotage/equipe", vue: "manager", roles: ROLES_PILOTAGE },
      { id: "taches", titre: "Tâches", description: "Tâches et imputations de l'équipe.", icone: ICONES.liste, route: "/taches", routeV2: "/v2/pilotage/taches", vue: "manager", roles: ROLES_PILOTAGE },
      { id: "dashboard", titre: "Dashboard", description: "Activité de l'équipe.", icone: ICONES.grille, route: "/dashboard", routeV2: "/v2/pilotage/dashboard", vue: "manager", roles: ROLES_PILOTAGE },
      { id: "projets", titre: "Projets", description: "Objectifs de l'équipe : avancement, retards, jalons à confirmer.", icone: ICONES.dossier, route: "/projets", routeV2: "/v2/pilotage/projets", vue: "manager", roles: ROLES_PILOTAGE },
      { id: "liens", titre: "Liens", description: "Applications affichées sur la page d'accueil.", icone: ICONES.annuaire, route: null, routeV2: "/v2/pilotage/liens", vue: "manager", roles: ["directeur"] },
    ],
  },
};

// --- Cartes de l'accueil, dans l'ordre d'affichage

export const ACCUEIL = [
  { type: "groupe", id: "activite" },
  { type: "module", id: "conversations", titre: "Conversations", description: "Échanges autour des projets.", icone: ICONES.bulle, route: "/conversations", routeV2: "/v2/conversations", vue: null, roles: null },
  { type: "module", id: "bibliotheques", titre: "Bibliothèques", description: "Fiches, procédures et référentiel technique.", icone: ICONES.livres, route: "/bibliotheques", routeV2: "/v2/bibliotheques", vue: null, roles: null },
  { type: "groupe", id: "utilisateurs" },
  { type: "module", id: "change-management", titre: "Change Management", description: "Demandes de changement et tickets CHG.", icone: ICONES.echange, route: "/change-management", routeV2: "/v2/change-management", vue: "manager", roles: null },
  { type: "groupe", id: "pilotage" },
];

// --- Aides

export function estVisible(entree, collaborateur) {
  if (!entree) return false;
  if (entree.roles && !entree.roles.includes(collaborateur?.role)) return false;
  if (entree.condition && !entree.condition(collaborateur)) return false;
  return true;
}

// Modules d'un regroupement visibles pour ce collaborateur
export function modulesVisibles(groupe, collaborateur) {
  return groupe.modules.filter((m) => estVisible(m, collaborateur));
}

// Entrees de l'accueil visibles pour ce collaborateur, regroupements resolus
export function accueilVisible(collaborateur) {
  return ACCUEIL.map((e) => (e.type === "groupe" ? { type: "groupe", ...GROUPES[e.id] } : e))
    .filter((e) => estVisible(e, collaborateur))
    .filter((e) => e.type !== "groupe" || modulesVisibles(e, collaborateur).length > 0);
}

// Module du catalogue par identifiant (accueil ou regroupement)
export function trouverModule(id) {
  const direct = ACCUEIL.find((e) => e.type === "module" && e.id === id);
  if (direct) return direct;
  for (const groupe of Object.values(GROUPES)) {
    for (const m of groupe.modules) {
      if (m.id === id) return m;
      const sous = (m.modules || []).find((x) => x.id === id);
      if (sous) return sous;
    }
  }
  return null;
}

// Fil d'Ariane d'une route v2 :
//   /v2                        -> []
//   /v2/pilotage               -> [Pilotage]
//   /v2/change-management      -> [Change Management]
//   route v2 d'un sous-module  -> [Regroupement (lien), Module]
//   sous-page d'un sous-module -> [Regroupement, Module (lien), libelle]
//     (ex. /v2/pilotage/equipe/12 -> Pilotage › Equipes › Fiche collaborateur)
const SOUS_PAGES = { equipe: "Fiche collaborateur" };

export function filArianePour(pathname) {
  // Refonte Projets : la fiche projet s'ouvre depuis plusieurs vues
  if (/^\/v2\/projets\/\d+$/.test(pathname)) return [{ libelle: "Fiche projet" }];
  for (const e of ACCUEIL) {
    if (e.type === "module" && e.routeV2 === pathname) return [{ libelle: e.titre }];
  }
  for (const groupe of Object.values(GROUPES)) {
    if (pathname === `/v2/${groupe.id}`) return [{ libelle: groupe.titre }];
    const m = groupe.modules.find((x) => x.routeV2 === pathname);
    if (m) return [{ libelle: groupe.titre, to: `/v2/${groupe.id}` }, { libelle: m.titre }];
    const parent = groupe.modules.find((x) => x.routeV2 && pathname.startsWith(`${x.routeV2}/`));
    if (parent) {
      const sous = (parent.modules || []).find((x) => x.routeV2 === pathname);
      return [
        { libelle: groupe.titre, to: `/v2/${groupe.id}` },
        { libelle: parent.titre, to: parent.routeV2 },
        { libelle: sous ? sous.titre : SOUS_PAGES[parent.id] || "Détail" },
      ];
    }
  }
  return [];
}

export const LIBELLES_ROLES = {
  directeur: "Directeur",
  manager: "Manager",
};
